import { useState } from 'react'

import { css, styled } from 'styled-components'

import Chip from 'core/components/lib/Chip/Chip'
import Clickable from 'core/components/lib/Clickable'
import { SelectOption } from 'core/components/lib/Select'
import { R } from 'core/helpers'
import { Field } from 'core/hooks/useForm'
import { outline, surfaceBright } from 'core/styles'

import Checkbox from './Checkbox'

type CheckboxGroupProps<T extends string | number> = Field<Array<T>> & {
  className?: string
  /** A list of label and value pairs corresponding to the checkboxes to display. */
  options: Array<SelectOption<T>>
  /** Include a checkbox to check or uncheck all values at once. */
  includeAll?: boolean
  /** The variant of the checkbox group. */
  variant?: 'default' | 'grid-with-border' | 'readonly'
  /** Number of columns when variant is `grid-with-border`. */
  columns?: number
  /** When set and options exceed this count, show only this many initially with a "See all (N more)" link. Default 10. */
  displayLimit?: number
}

/** A column of checkboxes with optional "All" checkbox. Avoids clobering `value`. */
const CheckboxGroup = <T extends string | number>({
  className,
  options,
  includeAll,
  name,
  value,
  disabled,
  onChange,
  variant = 'default',
  columns = 2,
  displayLimit = 10,
}: CheckboxGroupProps<T>) => {
  const [expanded, setExpanded] = useState(false)
  const checkboxVariant: 'default' | 'compact' | 'readonly' =
    variant === 'readonly' ? 'readonly'
    : variant === 'grid-with-border' ? 'compact'
    : 'default'
  const selectedCount = value?.length ?? 0

  const isLimited = options.length > displayLimit
  const visibleOptions = isLimited && !expanded ? R.take(options, displayLimit) : options
  const hiddenCount = options.length - displayLimit

  const allCheckboxProps = {
    disabled: disabled || variant === 'readonly' || options.every(R.prop('isDisabled')),
    name: `${name?.toString()}-all`,
    onChange: (checked: boolean) =>
      onChange?.(
        checked ?
          R.pipe(
            options,
            R.reject((opt) => !!opt.isDisabled),
            R.map(R.prop('value')),
            R.concat(value),
            R.uniq(),
          )
        : R.differenceWith(value, options, (val, opt) => opt.value === val),
      ),
    value: options.every((opt) => value.includes(opt.value)),
    variant: checkboxVariant,
  }

  return (
    <Column $columns={columns} $variant={variant} className={className}>
      {includeAll &&
        variant !== 'readonly' &&
        (variant === 'grid-with-border' ?
          <AllRow>
            <Checkbox label='Select all' {...allCheckboxProps} />
            <Chip $size='md' $variant='dim'>
              {selectedCount} Selected
            </Chip>
          </AllRow>
        : <Checkbox label='All' {...allCheckboxProps} />)}
      {variant === 'grid-with-border' || variant === 'readonly' ?
        <>
          <OptionsGrid $columns={columns} $variant={variant}>
            {R.map(visibleOptions, (option: SelectOption<T>) => (
              <StyledCheckbox
                disabled={disabled || variant === 'readonly' || option.isDisabled}
                key={option.value}
                label={option.label}
                name={`${name?.toString()}-${option.value}`}
                onChange={(checked) =>
                  onChange?.(
                    checked ?
                      R.uniq([...(value ?? []), option.value])
                    : R.reject(value ?? [], (v) => v === option.value),
                  )
                }
                sublabel={option.sublabel}
                value={value.includes(option.value)}
                variant={checkboxVariant}
              />
            ))}
          </OptionsGrid>
          {isLimited && variant !== 'readonly' && (
            <StyledClickable onClick={() => setExpanded((e) => !e)} variant='link'>
              {expanded ? 'Show less' : `See all (${hiddenCount} more)`}
            </StyledClickable>
          )}
        </>
      : R.map(visibleOptions, (option: SelectOption<T>) => (
          <StyledCheckbox
            $margin={includeAll ? '0 0 0 12px' : '0'}
            disabled={disabled || option.isDisabled}
            key={option.value}
            label={option.label}
            name={`${name?.toString()}-${option.value}`}
            onChange={(checked) =>
              onChange?.(
                checked ? R.uniq([...(value ?? []), option.value]) : R.reject(value ?? [], (v) => v === option.value),
              )
            }
            sublabel={option.sublabel}
            value={value.includes(option.value)}
            variant={checkboxVariant}
          />
        ))
      }
      {variant !== 'grid-with-border' && variant !== 'readonly' && isLimited && (
        <StyledClickable onClick={() => setExpanded((e) => !e)} variant='link'>
          {expanded ? 'Show less' : `See all (${hiddenCount} more)`}
        </StyledClickable>
      )}
    </Column>
  )
}

export default CheckboxGroup

const withBorder = css`
  box-sizing: border-box;
  border: 1px solid ${outline};
  border-radius: 8px;
  background: ${surfaceBright};
  padding: 6px 12px;
`

const Column = styled.div<{ $variant?: 'default' | 'grid-with-border' | 'readonly'; $columns?: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${(p) =>
    p.$variant === 'grid-with-border' &&
    css`
      ${withBorder};
    `}
`

const AllRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const OptionsGrid = styled.div<{ $columns?: number; $variant?: 'default' | 'grid-with-border' | 'readonly' }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$columns ?? 2}, 1fr);
  gap: 8px;

  ${(p) =>
    p.$variant === 'readonly' &&
    css`
      gap: 12px;
    `}
`

const StyledClickable = styled(Clickable)`
  font-weight: 400;
`

const StyledCheckbox = styled(Checkbox)<{ $margin?: string }>`
  margin: ${(p) => p.$margin ?? '0'};
`
